import React, { useState, useEffect, useCallback, useMemo } from 'react';
import withAuth from '../../../HOC/withAuth';
import { useTranslation } from '../../../t';
import { useParams } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import { isNumber } from '../../../util';
import { userStore } from '../../../stores/UserStore';
import { debounce } from 'lodash';
import dayjs from 'dayjs';
import OrderPreviewPanel from '../../../components/OrderPreviewPanel';
import StepShell from '../StepShell';
import C from '../../../colors';
import { hasOrderSelections } from '../../../orderStatus';

function SectionLabel({ children }) {
  return (
    <div style={{
      fontFamily: C.mono, fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase',
      color: C.brand, fontWeight: 600, marginBottom: 16, marginTop: 32,
    }}>{children}</div>
  );
}

function FormRow({ label, caption, children, inline, last, required = false }) {
  return (
    <div style={{
      padding: '18px 0',
      borderBottom: last ? 'none' : `1px solid ${C.hair}`,
      display: inline ? 'grid' : 'block',
      gridTemplateColumns: inline ? '1fr auto' : undefined,
      alignItems: inline ? 'center' : undefined,
      gap: 24,
    }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 500, color: C.ink, marginBottom: 3 }}>
          <span>{label}</span>
          {required ? (
            <span style={{ color: '#C2412E', fontSize: 13, fontWeight: 700 }}>*</span>
          ) : (
            <span style={{ fontSize: 11, color: C.ink3, fontWeight: 400 }}>选填</span>
          )}
        </div>
        {caption && <div style={{ fontSize: 12.5, color: C.ink3, lineHeight: 1.5, maxWidth: 520 }}>{caption}</div>}
      </div>
      {!inline && <div style={{ marginTop: 10 }}>{children}</div>}
      {inline && <div>{children}</div>}
    </div>
  );
}

function Toggle({ value, onChange, disabled, testId, label }) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-label={label}
      aria-pressed={value}
      onClick={() => !disabled && onChange(!value)}
      disabled={disabled}
      style={{
      width: 44, height: 24, borderRadius: 12,
      border: 'none', padding: 2,
      background: value ? C.brand : C.hair2,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.4 : 1,
      transition: 'background .2s',
      display: 'flex', alignItems: 'center',
      justifyContent: value ? 'flex-end' : 'flex-start',
    }}>
      <div style={{ width: 20, height: 20, borderRadius: 10, background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }}/>
    </button>
  );
}

function TextInput({ name, value, onChange, onBlur, placeholder, multiline, rows }) {
  const [focused, setFocused] = useState(false);
  const style = {
    width: '100%', boxSizing: 'border-box',
    padding: '9px 12px',
    border: `1px solid ${focused ? C.brand : C.hair2}`,
    borderRadius: 6, outline: 'none',
    fontFamily: C.sans, fontSize: 14, color: C.ink,
    background: '#fff', transition: 'border-color .15s',
    resize: multiline ? 'vertical' : undefined,
    minHeight: multiline ? 72 : undefined,
  };
  return multiline
    ? <textarea name={name} value={value || ''} onChange={onChange} onBlur={onBlur} placeholder={placeholder}
        rows={rows || 2} onFocus={() => setFocused(true)} onBlurCapture={() => setFocused(false)} style={style}/>
    : <input name={name} value={value || ''} onChange={onChange} onBlur={onBlur} placeholder={placeholder}
        onFocus={() => setFocused(true)} onBlurCapture={() => setFocused(false)} style={style}/>;
}

function NumberInput({ value, onChange, onBlur, prefix, suffix, disabled, error, helperText, name }) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <div style={{
        display: 'inline-flex', alignItems: 'center',
        border: `1px solid ${error ? '#e88c7c' : focused ? C.brand : C.hair2}`,
        borderRadius: 6, padding: '0 12px',
        background: disabled ? C.paper2 : '#fff',
        opacity: disabled ? 0.6 : 1,
        transition: 'border-color .15s', width: 220,
      }}>
        {prefix && <span style={{ fontSize: 14, color: C.ink3, marginRight: 4 }}>{prefix}</span>}
        <input type="number" name={name} value={value ?? ''} disabled={disabled}
          onChange={onChange} onBlur={onBlur}
          onFocus={() => setFocused(true)} onBlurCapture={() => setFocused(false)}
          style={{ flex: 1, border: 'none', outline: 'none', padding: '9px 0', fontSize: 14, fontFamily: C.sans, background: 'transparent', color: C.ink, fontVariantNumeric: 'tabular-nums' }}/>
        {suffix && <span style={{ fontSize: 14, color: C.ink3, marginLeft: 4 }}>{suffix}</span>}
      </div>
      {error && <div style={{ fontSize: 12, color: '#c2412e', marginTop: 4 }}>{error}</div>}
      {helperText && !error && <div style={{ fontSize: 12, color: C.ink3, marginTop: 4 }}>{helperText}</div>}
    </div>
  );
}

const Step1 = inject('snackbarStore', 'ordersStore', 'packageStore')(observer(({ snackbarStore, ordersStore, packageStore }) => {
  const { orderId } = useParams();
  const [order, setOrder] = useState({});
  const { t } = useTranslation();

  // Customer info
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [shootLocation, setShootLocation] = useState('');
  const [remark, setRemark] = useState('');
  const [shootTime, setShootTime] = useState('');
  const [packageId, setPackageId] = useState('');

  // Selection settings
  const [photoCount, setPhotoCount] = useState(0);
  const [photoCountError, setPhotoCountError] = useState('');
  const [forceLimit, setForceLimit] = useState(true);
  const [pricePerPic, setPricePerPic] = useState(0);
  const [pricePerPicError, setPricePerPicError] = useState('');
  const [showImageName, setShowImageName] = useState(false);
  const [isOnlinePayment, setIsOnlinePayment] = useState(false);

  const validatePhotoCount = (v) => {
    if (!v) return t('photoCount can not be empty');
    if (!isNumber(v)) return t('photoCount must be number');
    if (v > 1000) return t('photoCount must be less than 1000');
    return '';
  };
  const validatePricePerPic = (v) => (!isNumber(v) ? t('pricePerPic must be number') : '');

  const autoSaveRequest = useCallback((data) => {
    if (!order.id) return;
    ordersStore.updateOrder(order.id, data).then((response) => {
      const updatedOrder = response.data?.order || response.data;
      if (updatedOrder?.id) {
        setOrder(current => ({
          ...current,
          ...updatedOrder,
          ...(updatedOrder.packageId == null ? { package: null, Package: null } : {}),
        }));
      }
      snackbarStore.showSnackbar(t('Update Success'));
    }).catch(() => {});
  }, [order.id, snackbarStore, ordersStore, t]);

  const autoSave = useMemo(() => debounce(autoSaveRequest, 500), [autoSaveRequest]);

  const handleInfoBlur = (e) => {
    const { name, value } = e.target;
    autoSave({ [name]: value });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'photoCount') { setPhotoCount(value); setPhotoCountError(validatePhotoCount(value)); }
    else if (name === 'pricePerPic') { setPricePerPic(value); setPricePerPicError(validatePricePerPic(value)); }
    else if (name === 'customerName') setCustomerName(value);
    else if (name === 'customerPhone') setCustomerPhone(value);
    else if (name === 'shootLocation') setShootLocation(value);
    else if (name === 'remark') setRemark(value);
    else if (name === 'shootTime') { setShootTime(value); autoSave({ shootTime: value }); }
    else if (name === 'packageId') {
      setPackageId(value);
      const pkg = packageStore.packages.find(p => String(p.id) === value);
      setOrder(current => ({
        ...current,
        packageId: value || null,
        package: pkg || null,
        Package: pkg || null,
      }));
      if (pkg) { setPhotoCount(pkg.photoCount); autoSave({ packageId: value, photoCount: pkg.photoCount }); }
      else autoSave({ packageId: null });
    }
  };

  const handleInputBlur = (e) => {
    const { name, value } = e.target;
    if (name === 'photoCount') { const err = validatePhotoCount(value); if (!err) autoSave({ photoCount: value }); }
    else if (name === 'pricePerPic') { const err = validatePricePerPic(value); if (!err) autoSave({ pricePerPic: value }); }
  };

  useEffect(() => {
    if (!orderId) return;
    ordersStore.fetchOrder(orderId);
    packageStore.fetchPackages();
  }, [orderId]);

  useEffect(() => {
    if (!ordersStore.currentOrder) return;
    const o = ordersStore.currentOrder;
    setOrder(o);
    setCustomerName(o.customerName || '');
    setCustomerPhone(o.customerPhone || '');
    setShootLocation(o.shootLocation || '');
    setRemark(o.remark || '');
    setShootTime(o.shootTime ? dayjs(o.shootTime).format('YYYY-MM-DD') : '');
    setPackageId(o.packageId ? String(o.packageId) : '');
    setPhotoCount(o.photoCount);
    setForceLimit(o.forceLimit);
    setPricePerPic(o.pricePerPic);
    setShowImageName(o.showImageName || false);
    setIsOnlinePayment(o.isOnlinePayment || false);
  }, [ordersStore.currentOrder]);

  if (!order.id) return <div style={{ padding: 40, color: C.ink3 }}>Loading...</div>;

  const photoTotal = order.Album?.pictureCount || 0;
  const hasSelections = hasOrderSelections(order);

  return (
    <StepShell order={order}>
      <div style={{ padding: '24px 44px 64px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48, flex: 1 }}>

        {/* LEFT — form */}
        <div>
          {hasSelections && (
            <div data-testid="selection-settings-advisory" style={{ padding: '14px 18px', marginBottom: 4, background: C.warningBg, border: `1px solid ${C.warningDot}`, borderRadius: 8, fontSize: 13, color: C.warningFg }}>
              已有客户提交选图。你仍可修改设置，但调整选片数量、加片规则或支付方式前，请确认不会影响现有客户。
            </div>
          )}

          {/* ── 基本信息 ── */}
          <SectionLabel>基本信息</SectionLabel>

          {packageStore.packages.length > 0 && (
            <FormRow label={t('Packages')}>
              <select data-testid="order-package-select" name="packageId" value={packageId} onChange={handleInputChange} style={{
                width: '100%', padding: '9px 12px', borderRadius: 6,
                border: `1px solid ${C.hair2}`, background: '#fff',
                fontFamily: C.sans, fontSize: 14, color: C.ink, outline: 'none',
              }}>
                <option value="">— 不使用套餐 —</option>
                {packageStore.packages.map(p => (
                  <option key={p.id} value={String(p.id)}>{p.name} · {p.photoCount}{t('pics')}</option>
                ))}
              </select>
            </FormRow>
          )}

          <FormRow label={t('Customer Name')} required>
            <TextInput name="customerName" value={customerName} onChange={handleInputChange} onBlur={handleInfoBlur} placeholder={t('Customer Name')}/>
          </FormRow>

          <FormRow label={t('Customer Phone')}>
            <TextInput name="customerPhone" value={customerPhone} onChange={handleInputChange} onBlur={handleInfoBlur} placeholder={t('Customer Phone')}/>
          </FormRow>

          <FormRow label={t('Shoot Time')}>
            <input type="date" name="shootTime" value={shootTime} onChange={handleInputChange}
              style={{ padding: '9px 12px', borderRadius: 6, border: `1px solid ${C.hair2}`, fontFamily: C.sans, fontSize: 14, color: C.ink, outline: 'none', background: '#fff' }}/>
          </FormRow>

          <FormRow label={t('Shoot Location')}>
            <TextInput name="shootLocation" value={shootLocation} onChange={handleInputChange} onBlur={handleInfoBlur} placeholder={t('Shoot Location')} multiline/>
          </FormRow>

          <FormRow label={t('Remark')}>
            <TextInput name="remark" value={remark} onChange={handleInputChange} onBlur={handleInfoBlur} placeholder={t('Remark')} multiline rows={3}/>
          </FormRow>

          {/* ── 选片设置 ── */}
          <SectionLabel>选片设置</SectionLabel>

          <FormRow label={t('show image name')} caption="在照片缩略图下显示原始文件名，方便客户沟通指定照片。" inline>
            <Toggle testId="order-show-image-name-toggle" label={t('show image name')} value={showImageName} onChange={(v) => { setShowImageName(v); autoSave({ showImageName: v }); }}/>
          </FormRow>

          <FormRow label={t('selectable images')} caption="客户最多可以勾选的照片数量。" required>
            <NumberInput name="photoCount" value={photoCount} onChange={handleInputChange} onBlur={handleInputBlur}
              suffix={t('pics')} error={!!photoCountError} helperText={photoCountError || undefined}/>
          </FormRow>

          <FormRow label={t('force limit(no extra pics)')} caption="开启后客户必须选满上述数量才能提交。关闭则允许超选并按加片价格计费。" inline last={forceLimit}>
            <Toggle testId="order-allow-extra-toggle" label={t('force limit(no extra pics)')} value={!forceLimit} onChange={(v) => { setForceLimit(!v); autoSave({ forceLimit: !v }); }}/>
          </FormRow>

          {!forceLimit && (
            <>
              <FormRow label={t('append picture price')} caption="客户超出上限的每张照片将按此价格计费。">
                <NumberInput name="pricePerPic" value={pricePerPic} onChange={handleInputChange} onBlur={handleInputBlur}
                  prefix="¥" suffix={'/' + t('pics')} error={!!pricePerPicError} helperText={pricePerPicError || undefined}/>
              </FormRow>

              <FormRow label={t('Collected by platform')} caption={t('If you want to collect money from users, please link your WeChat account.')} inline last>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <Toggle testId="order-online-payment-toggle" label={t('Collected by platform')} value={isOnlinePayment} disabled={!userStore.wxOpenId}
                    onChange={(v) => { setIsOnlinePayment(v); autoSave({ isOnlinePayment: v }); }}/>
                  {!userStore.wxOpenId && (
                    <button onClick={() => userStore.setWechatDialogOpen(true)} style={{
                      padding: '8px 14px', borderRadius: 6, background: C.ink, color: '#fff',
                      border: 'none', fontFamily: C.sans, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                    }}>{t('Link WeChat')}</button>
                  )}
                </div>
              </FormRow>
            </>
          )}
        </div>

        {/* RIGHT — preview */}
        <OrderPreviewPanel showImageName={showImageName} forceLimit={forceLimit} isOnlinePayment={isOnlinePayment} price={pricePerPic} photoCount={photoCount} hasPackage={Boolean(packageId)}/>
      </div>
    </StepShell>
  );
}));

export default withAuth(Step1);
